// `avatar tool <enable|disable|list> [name]` — quản lý tool toggle.
//
// Tool toggle = gói tự-đủ trong pack (tools/<name>/) bật/tắt nguyên khối
// qua merge/rút manifest vào project settings.json. Khác hook hạ-tầng luôn-bật
// (scout-block, dev-rules-reminder nằm trong settings.json.tpl).
//
// Subcommands:
//   enable <name>   merge manifest hooks + deny vào settings.json + state enabled
//   disable <name>  rút đúng entry tool ra + state disabled
//   list            bảng: tool | available | enabled | version
//   add             multi-select tool available để cài (UX, bọc enable)
//   remove          multi-select tool đang bật để gỡ (UX, bọc disable)
//
// Flags: --target <path>; add/remove thêm --all (chọn hết, auto khi non-TTY) + --yes.
// Idempotent: enable lại không nhân đôi; disable tool chưa bật → no-op.
// add/remove tái dùng enableToolByName/disableToolByName (DRY).

import { resolve } from "node:path";
import { checkbox, confirm } from "@inquirer/prompts";
import type { Command } from "commander";
import {
  discoverEnabledAndAvailableTools,
  listAvailableTools,
} from "../lib/discover-pack-tools-and-defaults.js";
import { log } from "../lib/terminal-logger.js";
import {
  buildAddChoices,
  buildRemoveChoices,
  resolveNonInteractiveSelection,
} from "../lib/tool-add-remove-selection.js";
import { disableToolByName, enableToolByName } from "../lib/tool-enable-disable-orchestrator.js";
import { readToolState } from "../lib/tool-state-store.js";

interface ToolOptions {
  target?: string;
}

interface AddRemoveOptions extends ToolOptions {
  all?: boolean;
  yes?: boolean;
}

function resolveWorkspace(opts: ToolOptions): string {
  return resolve(opts.target ?? process.cwd());
}

// Quyết định selection: --all hoặc non-TTY → chọn hết candidate; ngược lại prompt
// checkbox. Trả về danh sách tên tool đã chọn (có thể rỗng).
async function selectTools(
  candidates: string[],
  preChecked: string[],
  opts: AddRemoveOptions,
  verb: "add" | "remove",
): Promise<string[]> {
  const nonInteractive = opts.all === true || !process.stdout.isTTY;
  if (nonInteractive) {
    return resolveNonInteractiveSelection(candidates);
  }
  const choices =
    verb === "add" ? buildAddChoices(candidates, preChecked) : buildRemoveChoices(candidates);
  return await checkbox<string>({
    message:
      verb === "add"
        ? "Chọn tool để cài (space chọn, enter xác nhận):"
        : "Chọn tool để gỡ (space chọn, enter xác nhận):",
    choices,
  });
}

// `avatar tool add` — list available, multi-select để enable 1-n.
async function runAdd(opts: AddRemoveOptions): Promise<void> {
  const ws = resolveWorkspace(opts);
  const { available, enabled } = await discoverEnabledAndAvailableTools(ws);
  if (available.length === 0) {
    log.dim("Không có tool available (pack chưa có tools/, hoặc chưa sync).");
    return;
  }
  const selected = await selectTools(available, enabled, opts, "add");
  if (selected.length === 0) {
    log.dim("Không chọn tool nào — hủy, không thay đổi.");
    return;
  }
  for (const name of selected) {
    await enableToolByName(ws, name);
  }
}

// `avatar tool remove` — list enabled, multi-select để disable 1-n.
async function runRemove(opts: AddRemoveOptions): Promise<void> {
  const ws = resolveWorkspace(opts);
  const { enabled } = await discoverEnabledAndAvailableTools(ws);
  if (enabled.length === 0) {
    log.dim("Không có tool nào đang bật — không có gì để gỡ.");
    return;
  }
  const selected = await selectTools(enabled, [], opts, "remove");
  if (selected.length === 0) {
    log.dim("Không chọn tool nào — hủy, không thay đổi.");
    return;
  }
  // remove sửa settings.json → xác nhận trước (trừ khi --yes hoặc non-TTY).
  const needConfirm = opts.yes !== true && opts.all !== true && process.stdout.isTTY;
  if (needConfirm) {
    const ok = await confirm({
      message: `Gỡ ${selected.length} tool: ${selected.join(", ")}?`,
      default: true,
    });
    if (!ok) {
      log.dim("Hủy — không gỡ tool nào.");
      return;
    }
  }
  for (const name of selected) {
    await disableToolByName(ws, name);
  }
}

async function runList(opts: ToolOptions): Promise<void> {
  const ws = resolveWorkspace(opts);
  const available = await listAvailableTools(ws);
  const state = await readToolState(ws);

  if (available.length === 0 && Object.keys(state.tools).length === 0) {
    log.dim("Không có tool nào (pack chưa có tools/, hoặc chưa sync). Chạy 'avatar sync'.");
    return;
  }

  // Union tên: tool có trong pack + tool từng ghi trong state.
  const names = [...new Set([...available, ...Object.keys(state.tools)])].sort();
  log.info("Tools:");
  log.plain(`  ${"NAME".padEnd(20)} ${"AVAILABLE".padEnd(10)} ${"ENABLED".padEnd(8)} VERSION`);
  for (const name of names) {
    const isAvailable = available.includes(name) ? "yes" : "no";
    const entry = state.tools[name];
    const enabled = entry?.enabled ? "yes" : "no";
    const version = entry?.version ?? "-";
    log.plain(`  ${name.padEnd(20)} ${isAvailable.padEnd(10)} ${enabled.padEnd(8)} ${version}`);
  }
}

export function registerToolsCommand(program: Command): void {
  const tools = program
    .command("tools")
    .description("Quản lý tool toggle (prompt-scoring, ...) bật/tắt nguyên khối");

  tools
    .command("enable <name>")
    .description("Bật tool: merge manifest vào settings.json + ghi state")
    .option("--target <path>", "Workspace root (default: cwd)")
    .action(async (name: string, opts: ToolOptions) => {
      const ws = resolveWorkspace(opts);
      const ok = await enableToolByName(ws, name);
      if (!ok) process.exit(1);
    });

  tools
    .command("disable <name>")
    .description("Tắt tool: rút đúng entry manifest ra khỏi settings.json + ghi state")
    .option("--target <path>", "Workspace root (default: cwd)")
    .action(async (name: string, opts: ToolOptions) => {
      const ws = resolveWorkspace(opts);
      await disableToolByName(ws, name);
    });

  tools
    .command("list")
    .description("Liệt kê tool: available | enabled | version")
    .option("--target <path>", "Workspace root (default: cwd)")
    .action(async (opts: ToolOptions) => {
      await runList(opts);
    });

  tools
    .command("add")
    .description("Chọn (multi-select) tool available để cài")
    .option("--target <path>", "Workspace root (default: cwd)")
    .option("--all", "Cài hết tool available (bỏ qua prompt; auto khi non-TTY)")
    .option("--yes", "Bỏ xác nhận")
    .action(async (opts: AddRemoveOptions) => {
      await runAdd(opts);
    });

  tools
    .command("remove")
    .description("Chọn (multi-select) tool đang bật để gỡ")
    .option("--target <path>", "Workspace root (default: cwd)")
    .option("--all", "Gỡ hết tool đang bật (bỏ qua prompt; auto khi non-TTY)")
    .option("--yes", "Bỏ xác nhận")
    .action(async (opts: AddRemoveOptions) => {
      await runRemove(opts);
    });
}
